import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Bus } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="login-card">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto flex items-center justify-center">
            <img src="/ntwanayam-logo.jpg" alt="NtwanaYam Logo" className="w-24 h-24 rounded-2xl object-cover shadow-md" data-testid="brand-logo" />
          </div>
          <CardTitle className="text-3xl font-heading" data-testid="login-title">Welcome to NtwanaYam</CardTitle>
          <CardDescription data-testid="login-description">Sign in to manage learner transport safely</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 rounded-full text-base font-medium"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-4">
            <Link to="/forgot-password" className="text-primary font-medium hover:underline" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </p>
          <p className="text-center text-sm text-muted-foreground mt-6">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
              Register here
            </Link>
          </p>
          <p className="text-center text-sm text-muted-foreground mt-3">
            <Link to="/about" className="text-primary font-medium hover:underline" data-testid="about-link">
              About NtwanaYam
            </Link>
            <span className="mx-2 text-muted-foreground">·</span>
            <Link to="/terms" className="text-primary font-medium hover:underline" data-testid="terms-link">
              Terms &amp; Legal
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
